import React, { createContext, useContext, useState, useEffect } from 'react';
import apiService from '../apiService';
import { useAlert } from './AlertContext';

// Simple fuzzy search utility
const fuzzyMatch = (str, searchTerm) => {
    if (!searchTerm || !str) return 0;
    const normalize = s => s.toLowerCase().replace(/[^a-z0-9]/g, '');
    const normalizedStr = normalize(str);
    const normalizedSearch = normalize(searchTerm);
    
    if (normalizedSearch.length === 0) return 1;
    if (normalizedStr.includes(normalizedSearch)) return 1;
    
    let matches = 0;
    for (let i = 0; i < normalizedSearch.length; i++) {
        if (normalizedStr.includes(normalizedSearch[i])) matches++;
    }
    const similarity = matches / Math.max(normalizedSearch.length, normalizedStr.length);
    return similarity >= 0.5 ? similarity : 0;
};

// --- Patient Context ---
const PatientContext = createContext();

export const PatientProvider = ({ children, onLogout }) => {
    const [patients, setPatients] = useState([]); // Keep for backward compatibility
    const [paginatedPatients, setPaginatedPatients] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchField, setSearchField] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const itemsPerPage = 50; // Larger page size for backend pagination
    const { setAlert } = useAlert();

    // Fetch paginated patients from backend
    useEffect(() => {
        const fetchPatients = async () => {
            setIsLoading(true);
            try {
                const data = await apiService.getPatientsPaginated({
                    page: currentPage,
                    pageSize: itemsPerPage,
                    searchTerm: searchTerm || null,
                    searchField: searchField || null,
                });
                setPaginatedPatients(data.items);
                setTotal(data.total);
                setTotalPages(Math.ceil(data.total / itemsPerPage));
            } catch (error) {
                console.error('Error fetching patients in PatientProvider:', error);
                if (typeof setAlert === 'function') {
                    if (error.message.includes('UNAUTHORIZED')) {
                        setAlert({ message: 'Session expired. Please log in again.', type: 'error' });
                        onLogout();
                    } else {
                        setAlert({ message: 'Error fetching patients: ' + error.message, type: 'error' });
                    }
                } else {
                    console.error('setAlert is not a function in PatientProvider');
                }
            } finally {
                setIsLoading(false);
            }
        };
        fetchPatients();
    }, [currentPage, searchTerm, searchField, onLogout, setAlert]);

    const addPatient = async (patientData) => {
        try {
            const newPatient = await apiService.addPatient(patientData);
            // Refresh the current page
            setCurrentPage(1); // Go to first page to see the new patient
            if (typeof setAlert === 'function') {
                setAlert({ message: 'Patient added successfully', type: 'success' });
            } else {
                console.error('setAlert is not a function in addPatient');
            }
            return newPatient;
        } catch (error) {
            console.error('Error adding patient:', error);
            if (typeof setAlert === 'function') {
                if (error.message.includes('UNAUTHORIZED')) {
                    setAlert({ message: 'Session expired. Please log in again.', type: 'error' });
                    onLogout();
                } else {
                    setAlert({ message: 'Error adding patient: ' + error.message, type: 'error' });
                }
            } else {
                console.error('setAlert is not a function in addPatient');
            }
            throw error;
        }
    };

    const updatePatient = async (updatedPatient) => {
        try {
            const result = await apiService.updatePatient(updatedPatient);
            // Update in local state
            setPaginatedPatients(prev => prev.map(p => (p.id === result.id ? result : p)));
            if (typeof setAlert === 'function') {
                setAlert({ message: 'Patient updated successfully', type: 'success' });
            } else {
                console.error('setAlert is not a function in updatePatient');
            }
            return result;
        } catch (error) {
            console.error('Error updating patient:', error);
            if (typeof setAlert === 'function') {
                if (error.message.includes('UNAUTHORIZED')) {
                    setAlert({ message: 'Session expired. Please log in again.', type: 'error' });
                    onLogout();
                } else {
                    setAlert({ message: 'Error updating patient: ' + error.message, type: 'error' });
                }
            } else {
                console.error('setAlert is not a function in updatePatient');
            }
            throw error;
        }
    };

    return (
        <PatientContext.Provider
            value={{
                patients: paginatedPatients, // For backward compatibility
                setPatients: setPaginatedPatients,
                addPatient,
                updatePatient,
                searchTerm,
                setSearchTerm,
                searchField,
                setSearchField,
                currentPage,
                setCurrentPage,
                totalPages,
                total,
                paginatedPatients,
                isLoading,
            }}
        >
            {children}
        </PatientContext.Provider>
    );
};

export const usePatients = () => {
    const context = useContext(PatientContext);
    if (!context) {
        throw new Error('usePatients must be used within a PatientProvider');
    }
    return context;
};

// --- Appointment Context ---
const AppointmentContext = createContext();

export const AppointmentProvider = ({ children, onLogout }) => {
    const [appointments, setAppointments] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchField, setSearchField] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    const { setAlert } = useAlert();

    useEffect(() => {
        const fetchAppointments = async () => {
            try {
                const data = await apiService.getAppointments();
                setAppointments(data);
            } catch (error) {
                console.error('Error fetching appointments in AppointmentProvider:', error);
                if (typeof setAlert === 'function') {
                    if (error.message.includes('UNAUTHORIZED')) {
                        setAlert({ message: 'Session expired. Please log in again.', type: 'error' });
                        onLogout();
                    } else {
                        setAlert({ message: 'Error fetching appointments: ' + error.message, type: 'error' });
                    }
                } else {
                    console.error('setAlert is not a function in AppointmentProvider');
                }
            }
        };
        fetchAppointments();
    }, [onLogout, setAlert]);

    const addAppointment = async (appointmentData) => {
        try {
            const newAppointment = await apiService.addAppointment(appointmentData);
            setAppointments(prev => [...prev, newAppointment]);
            if (typeof setAlert === 'function') {
                setAlert({ message: 'Appointment added successfully', type: 'success' });
            } else {
                console.error('setAlert is not a function in addAppointment');
            }
            return newAppointment;
        } catch (error) {
            console.error('Error adding appointment:', error);
            if (typeof setAlert === 'function') {
                if (error.message.includes('UNAUTHORIZED')) {
                    setAlert({ message: 'Session expired. Please log in again.', type: 'error' });
                    onLogout();
                } else {
                    setAlert({ message: 'Error adding appointment: ' + error.message, type: 'error' });
                }
            } else {
                console.error('setAlert is not a function in addAppointment');
            }
            throw error;
        }
    };

    const updateAppointment = async (updatedAppointment) => {
        try {
            const result = await apiService.updateAppointment(updatedAppointment);
            setAppointments(prev => prev.map(a => (a.id === result.id ? result : a)));
            if (typeof setAlert === 'function') {
                setAlert({ message: 'Appointment updated successfully', type: 'success' });
            } else {
                console.error('setAlert is not a function in updateAppointment');
            }
            return result;
        } catch (error) {
            console.error('Error updating appointment:', error);
            if (typeof setAlert === 'function') {
                if (error.message.includes('UNAUTHORIZED')) {
                    setAlert({ message: 'Session expired. Please log in again.', type: 'error' });
                    onLogout();
                } else {
                    setAlert({ message: 'Error updating appointment: ' + error.message, type: 'error' });
                }
            } else {
                console.error('setAlert is not a function in updateAppointment');
            }
            throw error;
        }
    };

    const filteredAppointments = appointments.filter(appointment => {
        if (!searchTerm) return true;
        if (searchField === 'all') {
            return (
                fuzzyMatch(appointment.patient, searchTerm) > 0 ||
                fuzzyMatch(appointment.doctor, searchTerm) > 0 ||
                fuzzyMatch(appointment.department, searchTerm) > 0
            );
        }
        if (searchField === 'patient') {
            return fuzzyMatch(appointment.patient, searchTerm) > 0;
        }
        if (searchField === 'doctor') {
            return fuzzyMatch(appointment.doctor, searchTerm) > 0;
        }
        if (searchField === 'department') {
            return fuzzyMatch(appointment.department, searchTerm) > 0;
        }
        return true;
    });

    const totalPages = Math.ceil(filteredAppointments.length / itemsPerPage);
    const paginatedAppointments = filteredAppointments.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    return (
        <AppointmentContext.Provider
            value={{
                appointments,
                setAppointments,
                addAppointment,
                updateAppointment,
                searchTerm,
                setSearchTerm,
                searchField,
                setSearchField,
                currentPage,
                setCurrentPage,
                totalPages,
                paginatedAppointments,
            }}
        >
            {children}
        </AppointmentContext.Provider>
    );
};

export const useAppointments = () => {
    const context = useContext(AppointmentContext);
    if (!context) {
        throw new Error('useAppointments must be used within an AppointmentProvider');
    }
    return context;
};

// --- Staff Context ---
const StaffContext = createContext();

export const StaffProvider = ({ children, onLogout }) => {
    const [staff, setStaff] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchField, setSearchField] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    const { setAlert } = useAlert();

    useEffect(() => {
        const fetchStaff = async () => {
            try {
                const data = await apiService.getStaff();
                setStaff(data);
            } catch (error) {
                console.error('Error fetching staff in StaffProvider:', error);
                if (typeof setAlert === 'function') {
                    if (error.message.includes('UNAUTHORIZED')) {
                        setAlert({ message: 'Session expired. Please log in again.', type: 'error' });
                        onLogout();
                    } else {
                        setAlert({ message: 'Error fetching staff: ' + error.message, type: 'error' });
                    }
                } else {
                    console.error('setAlert is not a function in StaffProvider');
                }
            }
        };
        fetchStaff();
    }, [onLogout, setAlert]);

    const addStaff = async (staffData) => {
        try {
            const newStaff = await apiService.addStaffMember(staffData);
            setStaff(prev => [...prev, newStaff]);
            if (typeof setAlert === 'function') {
                setAlert({ message: 'Staff member added successfully', type: 'success' });
            } else {
                console.error('setAlert is not a function in addStaff');
            }
            return newStaff;
        } catch (error) {
            console.error('Error adding staff member:', error);
            if (typeof setAlert === 'function') {
                if (error.message.includes('UNAUTHORIZED')) {
                    setAlert({ message: 'Session expired. Please log in again.', type: 'error' });
                    onLogout();
                } else {
                    setAlert({ message: 'Error adding staff member: ' + error.message, type: 'error' });
                }
            } else {
                console.error('setAlert is not a function in addStaff');
            }
            throw error;
        }
    };

    const filteredStaff = staff.filter(member => {
        if (!searchTerm) return true;
        if (searchField === 'all') {
            return (
                fuzzyMatch(member.name, searchTerm) > 0 ||
                (member.specialization && fuzzyMatch(member.specialization, searchTerm) > 0) ||
                fuzzyMatch(member.department, searchTerm) > 0
            );
        }
        if (searchField === 'name') {
            return fuzzyMatch(member.name, searchTerm) > 0;
        }
        if (searchField === 'specialization') {
            return member.specialization && fuzzyMatch(member.specialization, searchTerm) > 0;
        }
        if (searchField === 'department') {
            return fuzzyMatch(member.department, searchTerm) > 0;
        }
        return true;
    });

    const totalPages = Math.ceil(filteredStaff.length / itemsPerPage);
    const paginatedStaff = filteredStaff.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    return (
        <StaffContext.Provider
            value={{
                staff,
                setStaff,
                addStaff,
                searchTerm,
                setSearchTerm,
                searchField,
                setSearchField,
                currentPage,
                setCurrentPage,
                totalPages,
                paginatedStaff,
            }}
        >
            {children}
        </StaffContext.Provider>
    );
};

export const useStaff = () => {
    const context = useContext(StaffContext);
    if (!context) {
        throw new Error('useStaff must be used within a StaffProvider');
    }
    return context;
};

// --- Inventory Context ---
const InventoryContext = createContext();

export const InventoryProvider = ({ children, onLogout }) => {
    const [inventoryItems, setInventoryItems] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchField, setSearchField] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    const { setAlert } = useAlert();

    useEffect(() => {
        const fetchInventoryItems = async () => {
            try {
                const data = await apiService.getInventoryItems();
                setInventoryItems(data);
            } catch (error) {
                console.error('Error fetching inventory items in InventoryProvider:', error);
                if (typeof setAlert === 'function') {
                    if (error.message.includes('UNAUTHORIZED')) {
                        setAlert({ message: 'Session expired. Please log in again.', type: 'error' });
                        onLogout();
                    } else {
                        setAlert({ message: 'Error fetching inventory items: ' + error.message, type: 'error' });
                    }
                } else {
                    console.error('setAlert is not a function in InventoryProvider');
                }
            }
        };
        fetchInventoryItems();
    }, [onLogout, setAlert]);

    const addInventoryItem = async (itemData) => {
        try {
            const newItem = await apiService.addInventoryItem(itemData);
            setInventoryItems(prev => [...prev, newItem]);
            if (typeof setAlert === 'function') {
                setAlert({ message: 'Inventory item added successfully', type: 'success' });
            } else {
                console.error('setAlert is not a function in addInventoryItem');
            }
            return newItem;
        } catch (error) {
            console.error('Error adding inventory item:', error);
            if (typeof setAlert === 'function') {
                if (error.message.includes('UNAUTHORIZED')) {
                    setAlert({ message: 'Session expired. Please log in again.', type: 'error' });
                    onLogout();
                } else {
                    setAlert({ message: 'Error adding inventory item: ' + error.message, type: 'error' });
                }
            } else {
                console.error('setAlert is not a function in addInventoryItem');
            }
            throw error;
        }
    };

    const filteredInventoryItems = inventoryItems.filter(item => {
        if (!searchTerm) return true;
        if (searchField === 'all') {
            return (
                fuzzyMatch(item.name, searchTerm) > 0 ||
                fuzzyMatch(item.itemType, searchTerm) > 0 ||
                fuzzyMatch(item.location, searchTerm) > 0
            );
        }
        if (searchField === 'name') {
            return fuzzyMatch(item.name, searchTerm) > 0;
        }
        if (searchField === 'type') {
            return fuzzyMatch(item.itemType, searchTerm) > 0;
        }
        if (searchField === 'location') {
            return fuzzyMatch(item.location, searchTerm) > 0;
        }
        return true;
    });

    const totalPages = Math.ceil(filteredInventoryItems.length / itemsPerPage);
    const paginatedInventoryItems = filteredInventoryItems.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    return (
        <InventoryContext.Provider
            value={{
                inventoryItems,
                setInventoryItems,
                addInventoryItem,
                searchTerm,
                setSearchTerm,
                searchField,
                setSearchField,
                currentPage,
                setCurrentPage,
                totalPages,
                paginatedInventoryItems,
            }}
        >
            {children}
        </InventoryContext.Provider>
    );
};

export const useInventory = () => {
    const context = useContext(InventoryContext);
    if (!context) {
        throw new Error('useInventory must be used within an InventoryProvider');
    }
    return context;
};